import React, { useState, useEffect } from "react";
declare global {
  interface Window {
    general_settings?: {
      logo_dark?: string;
    };
  }
}

interface FooterProps {
  isActive?: boolean;
}

const Footer: React.FC<FooterProps> = ({ isActive = true }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const template = (window as any)?.post_template || {};
  const footer = template.wellanail_flex_footer || {};

  const logoUrl = footer.tr_footer_logo || window?.general_settings?.logo_dark;

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
      setIsTablet(width <= 1091);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Base styles
  const footerStyles: React.CSSProperties = {
    backgroundColor: "#fff",
    fontFamily: '"Open sans", sans-serif',
    paddingTop: isMobile ? "0px" : "24px",
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: "1000px",
    margin: "0 auto",
    paddingLeft: isTablet ? "16px" : "0",
    paddingRight: isTablet ? "16px" : "0",
  };

  const contentStyles: React.CSSProperties = {
    color: "var(--footer-disclaimer-text-color, #828282)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "16px",
    padding: "12px 13px",
    marginBottom: "16px",
    border: "1px solid var(--footer-divider-color, #f2f2f2)",
    textAlign: isMobile ? "center" : "left",
    marginTop: isMobile ? "0" : undefined,
  };

  // Menu styles
  const getMenuStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      borderTop: "1px solid var(--footer-divider-color, #f2f2f2)",
      padding: isMobile ? "16px 0" : "24px 0 16px",
    };

    if (isMobile || isTablet) {
      return {
        ...base,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      };
    }

    return {
      ...base,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    };
  };

  const logoStyles: React.CSSProperties = {
    width: isMobile
      ? "var(--logo-width-mobile, 147px)"
      : "var(--logo-width-desktop, 178px)",
    height: "24px",
  };

  const menuLinksContainerStyles: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    marginTop: isMobile || isTablet ? "16px" : "0",
  };

  const linkPaddingStyles: React.CSSProperties = {
    paddingLeft: isMobile ? "8px" : "0",
    paddingRight: isMobile ? "8px" : "0",
  };

  const menuItemStyles: React.CSSProperties = {
    padding: "8px 0",
  };

  const menuLinkStyles: React.CSSProperties = {
    color: "var(--footer-link-text-color, #4F4F4F)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "24px",
    transition: "color 150ms ease-in",
    textDecoration: "none",
  };

  const menuLinkHoverStyles: React.CSSProperties = {
    color: "var(--footer-link-text-hover-color, #4F4F4F)",
  };

  // Back to top button styles
  const getBackToTopButtonStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: "flex",
      padding: "8px 16px",
      alignItems: "center",
      borderRadius: "100px",
      border: "none",
      backgroundColor: "#232f3e",
      color: "#fff",
      textAlign: "center",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: "700",
      lineHeight: "16px",
      textDecoration: "none",
      cursor: "pointer",
      textTransform: "capitalize",
    };

    if (isMobile) {
      return {
        ...base,
        marginBottom: "16px",
        marginTop: "0",
      };
    }

    return {
      ...base,
      marginTop: "0",
    };
  };

  const backToTopSpanStyles: React.CSSProperties = {
    display: "flex",
  };

  const backToTopArrowStyles: React.CSSProperties = {
    marginLeft: "8px",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='ei:arrow-up'%3E%3Cpath id='Vector' d='M0.999849 8.27827C0.999849 4.40764 4.12929 1.2782 7.99992 1.2782C11.8706 1.2782 15 4.40764 15 8.27827C15 12.1489 11.8706 15.2783 7.99992 15.2783C4.12929 15.2783 0.999849 12.1489 0.999849 8.27827ZM14.1765 8.27827C14.1765 4.86059 11.4176 2.10174 7.99992 2.10174C4.58224 2.10174 1.82339 4.86059 1.82339 8.27827C1.82339 11.696 4.58224 14.4548 7.99992 14.4548C11.4176 14.4548 14.1765 11.696 14.1765 8.27827Z' fill='%23ffffff'/%3E%3Cpath id='Vector_2' d='M7.29997 11.696L10.7177 8.27828L7.29997 4.8606L7.87645 4.28412L11.8706 8.27828L7.87644 12.2724L7.29997 11.696Z' fill='%23ffffff'/%3E%3Cpath id='Vector_3' d='M11.2942 7.86646L11.2942 8.68999L4.29411 8.68999L4.29411 7.86645L11.2942 7.86646Z' fill='%23ffffff'/%3E%3C/g%3E%3C/svg%3E")`,
    height: "16px",
    width: "16px",
    transform: "rotate(-90deg)",
  };

  // Copyright styles
  const getCopyrightStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      borderTop: "1px solid var(--footer-divider-color, #f2f2f2)",
      padding: isMobile ? "16px 0" : "24px 0",
    };

    if (isMobile) {
      return {
        ...base,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px 0 24px",
      };
    }

    return {
      ...base,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    };
  };

  const copyrightTextStyles: React.CSSProperties = {
    color: "var(--gray-2, #4f4f4f)",
    fontSize: isMobile ? "14px" : "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "24px",
    marginBottom: "0",
    textAlign: isMobile ? "center" : "left",
  };

  // Menu items — hidden when the "Hide this link" ACF toggle is checked
  const menuItems = [
    {
      label: footer.tr_footer_menu_1_label || "About Us",
      href:
        footer.tr_footer_menu_1_href ||
        "https://testproduct.review/legal/about-us/",
      hidden:
        footer.tr_footer_menu_1_hidden || footer.tr_footer_menu_1_label === "",
    },
    {
      label: footer.tr_footer_menu_2_label || "Terms of Use",
      href:
        footer.tr_footer_menu_2_href ||
        "https://testproduct.review/legal/terms-of-use/",
      hidden:
        footer.tr_footer_menu_2_hidden || footer.tr_footer_menu_2_label === "",
    },
    {
      label: footer.tr_footer_menu_3_label || "Privacy Policy",
      href:
        footer.tr_footer_menu_3_href ||
        "https://testproduct.review/legal/privacy-policy/",
      hidden:
        footer.tr_footer_menu_3_hidden || footer.tr_footer_menu_3_label === "",
    },
    {
      label: footer.tr_footer_menu_4_label || "Advertising Disclosure",
      href:
        footer.tr_footer_menu_4_href ||
        "https://testproduct.review/legal/advertising-disclosure/",
      hidden:
        footer.tr_footer_menu_4_hidden || footer.tr_footer_menu_4_label === "",
    },
    {
      label: footer.tr_footer_menu_5_label || "Contact us",
      href:
        footer.tr_footer_menu_5_href ||
        "https://testproduct.review/legal/contact-us/",
      hidden:
        footer.tr_footer_menu_5_hidden || footer.tr_footer_menu_5_label === "",
    },
  ].filter((item) => !item.hidden);

  return (
    <footer
      id="footer"
      className={`footer ${isActive ? "floaterActive" : ""}`}
      style={footerStyles}
    >
      <div style={containerStyles}>
        {/* Disclaimer content */}
        <div style={contentStyles}>
          <p>
            {footer.tr_footer_disclaimer ||
              "This site is a free online resource that strives to offer helpful content and comparison features to its visitors. Please be advised that the operator of this site accepts advertising compensation from companies that appear on the site, and such compensation impacts the location and order in which the companies (and/or their products) are presented, and in some cases may also impact the rating that is assigned to them. To the extent that ratings appear on this site, such rating is determined by our subjective opinion and based on a methodology that aggregates our analysis of brand market share and reputation, each brand's conversion rates, compensation paid to us and general consumer interest. Company listings on this page DO NOT imply endorsement. Except as expressly set forth in our Terms of Use, all representations and warranties regarding the information presented on this page are disclaimed. The information, including pricing, which appears on this site is subject to change at any time."}
          </p>
        </div>

        {/* Menu section */}
        <div style={getMenuStyles()}>
          {/* Logo */}
          <div style={logoStyles}>
            <img
              src={logoUrl}
              alt="TestProduct"
              style={{ height: "100%", width: "100%" }}
              className="p-1"
            />
          </div>

          {/* Menu links - Using row-cols structure from original HTML */}
          <div style={menuLinksContainerStyles}>
            {menuItems.map((item, index) => (
              <div key={index} style={{ ...linkPaddingStyles }}>
                <div style={menuItemStyles}>
                  <a
                    href={item.href}
                    style={menuLinkStyles}
                    onMouseEnter={(e) =>
                      Object.assign(e.currentTarget.style, menuLinkHoverStyles)
                    }
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = menuLinkStyles.color || "";
                    }}
                  >
                    {item.label}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Back to Top button */}
          <button onClick={handleBackToTop} style={getBackToTopButtonStyles()}>
            <span style={backToTopSpanStyles}>
              {footer.tr_back_to_top_label || "Back To Top"}
              <span style={backToTopArrowStyles}></span>
            </span>
          </button>
        </div>

        {/* Copyright section */}
        <div style={getCopyrightStyles()}>
          <p style={copyrightTextStyles}>
            {footer.tr_footer_copyright ||
              "Copyright \u00a9 2026 - TestProduct All Rights Reserved"}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
